import { createContext, Dispatch } from 'react';
import { ErrorLike } from '@apollo/client';

import {
  initialTreeState,
  SubItemTreeAction,
  SubItemTreeState,
} from '@atom/components/common/subItemTree/subItemTreeReducer';
import {
  AssetAttributeChangeInput,
  AssetAttributesUpdateInput,
  AssetDetail,
  AssetUpdateInput,
  ChangeType,
} from '@atom/types/asset';
import { ConversionUnit } from '@atom/types/schema';

import { InventoryDetailAction } from './inventoryDetailReducer';

export type UpdateAssetOptions = {
  includeCursor?: boolean;
};

export type ConversionUnitSelection = {
  assetId: string;
  conversionUnitId: string;
};

export type InventoryDetailContextValue = {
  asset: AssetDetail;
  dispatch: Dispatch<InventoryDetailAction>;
  loading: boolean;
  error?: ErrorLike;
  refetch: () => Promise<AssetDetail>;
  updateAsset: (
    input: AssetUpdateInput,
    options?: UpdateAssetOptions,
  ) => Promise<void>;
  updateAttributes: (input: AssetAttributesUpdateInput) => Promise<void>;
  deleteAsset: () => Promise<void>;
  approveAllChanges: () => Promise<void>;
  approveAttributeChange: (input: AssetAttributeChangeInput) => Promise<void>;
  rejectAttributeChange: (input: AssetAttributeChangeInput) => Promise<void>;
  subItemTree: SubItemTreeState;
  subItemTreeDispatch: Dispatch<SubItemTreeAction>;
  fetchAssetDetail: (id: string) => Promise<AssetDetail>;
  loadNode: (id: string) => Promise<AssetDetail | null>;
  refetchNodes: (ids: string[]) => Promise<AssetDetail[]>;
  refreshChangeTypes: (assetIds: string[]) => Promise<ChangeType | null>;
  refreshSubItemTree: (
    rootDetail?: AssetDetail,
    removedIds?: string[],
  ) => Promise<void>;
  conversionUnits: ConversionUnit[];
  conversionUnitSelection?: ConversionUnitSelection;
  setConversionUnitSelection: (selection: ConversionUnitSelection) => void;
};

export const initialState: InventoryDetailContextValue = {
  asset: {} as AssetDetail,
  dispatch: () => {},
  loading: true,
  error: undefined,
  refetch: () => Promise.resolve(null),
  updateAsset: () => Promise.resolve(),
  updateAttributes: () => Promise.resolve(),
  deleteAsset: () => Promise.resolve(),
  approveAllChanges: () => Promise.resolve(),
  approveAttributeChange: () => Promise.resolve(),
  rejectAttributeChange: () => Promise.resolve(),
  subItemTree: initialTreeState,
  subItemTreeDispatch: () => {},
  fetchAssetDetail: () => Promise.resolve({} as AssetDetail),
  loadNode: () => Promise.resolve(null),
  refetchNodes: () => Promise.resolve([]),
  refreshChangeTypes: () => Promise.resolve(null),
  refreshSubItemTree: () => Promise.resolve(),
  conversionUnits: [],
  conversionUnitSelection: undefined,
  setConversionUnitSelection: () => {},
};

const InventoryDetailContext =
  createContext<InventoryDetailContextValue>(initialState);

export default InventoryDetailContext;
